import React from 'react';
import { LocationSummaryData, TaskGraphDataPoint } from '../../types';
import { NavigateToRouteWithDashboardParams } from '@Helpers/routing';
import { Routes } from '@Constants/routes';
import { DATE_FORMAT } from '@Constants/constants';
import Moment from 'moment';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import NivoTaskCompletionBar from './NivoTaskCompletionBar';
import ScoreBlock from './ScoreBlock';
import './LocationSummaryTile.scss';

export const expandToggleId = (locationId: number): string => `lst-expand-toggle-${locationId}`;

interface LocationSummaryTileProps {
    location: LocationSummaryData;
    onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
    isActive: boolean;
    isLoadingMetrics?: boolean;
    // The Assign Tasks drawer is owned by LocationsList, not by the tile: long lists are
    // virtualized, and a tile scrolled out of the window unmounts — taking an open drawer
    // and its in-progress assignments with it.
    onAssignTasks: () => void;
    selectedProjectIds: number[];
    isExpanded?: boolean;
    onToggleExpand?: () => void;
}

const LocationSummaryTile: React.FC<LocationSummaryTileProps> = ({
    location,
    onClick,
    isActive,
    isLoadingMetrics,
    onAssignTasks,
    selectedProjectIds,
    isExpanded = false,
    onToggleExpand,
}) => {
    // ---- navigation helpers ----

    // Every drill-down leaves the Overview for the Tasks page, which no longer has the
    // Overview's project filter — carry the selected projects along so the task list opens
    // scoped the same way the tile was.
    const withSelectedProjectParams = (params?: Record<string, string[]>): Record<string, string[]> | undefined => {
        const projectIdsAsStrings = (selectedProjectIds ?? []).map((id) => `${id}`);
        if (projectIdsAsStrings.length === 0) return params;

        // TASK_LIST_URL_PARAMS maps both `projectId` and `projectIds` onto the same `p` query
        // arg, so injecting ours on top of a payload that already scopes projects would emit two
        // conflicting values for a single filter. Only fill in when the backend left it out.
        if (params && ('projectIds' in params || 'projectId' in params)) return params;

        return { ...(params ?? {}), projectIds: projectIdsAsStrings };
    };

    const onOverallCompleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (location.overallCompleteUrlParams) {
            NavigateToRouteWithDashboardParams(Routes.Tasks, withSelectedProjectParams(location.overallCompleteUrlParams), false);
        }
    };

    const onPastDueClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (location.pastDueUrlParams) {
            NavigateToRouteWithDashboardParams(Routes.Tasks, withSelectedProjectParams(location.pastDueUrlParams), false);
        }
    };

    const onCompleteToDateClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (location.completeToDateUrlParams) {
            NavigateToRouteWithDashboardParams(Routes.Tasks, withSelectedProjectParams(location.completeToDateUrlParams), false);
        }
    };

    const onGraphBarClick = (dataPoint: TaskGraphDataPoint) => {
        if (dataPoint?.urlParams) {
            NavigateToRouteWithDashboardParams(Routes.Tasks, withSelectedProjectParams(dataPoint.urlParams), false);
        }
    };

    const resolveMilestoneUrlParams = (type: 'pastDue' | 'upcoming'): Record<string, string[]> => {
        const source = location as unknown as Record<string, Record<string, string[]> | undefined>;
        const candidateKeys =
            type === 'pastDue'
                ? ['pastDueMilestoneUrlParams', 'pastDueMilestonesUrlParams']
                : ['upcomingMilestoneUrlParams', 'upcomingMilestonesUrlParams'];
        const today = Moment.utc().format(DATE_FORMAT);
        const fourteenDaysOut = Moment.utc().add(14, 'days').format(DATE_FORMAT);
        let base: Record<string, string[]> = {
            locationKeys: [location.locationKey],
        };

        for (const key of candidateKeys) {
            const value = source[key];
            if (value) {
                base = value;
                break;
            }
        }

        return {
            ...base,
            isMilestone: ['true'],
            ...(type === 'upcoming'
                ? { targetDateStart: [today], targetDateEnd: [fourteenDaysOut] }
                : { targetDateEnd: [today] }),
        };
    };

    const onPastDueMilestoneClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const urlParams = resolveMilestoneUrlParams('pastDue');
        NavigateToRouteWithDashboardParams(Routes.Tasks, withSelectedProjectParams(urlParams), false);
    };

    const onUpcomingMilestoneClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const urlParams = resolveMilestoneUrlParams('upcoming');
        NavigateToRouteWithDashboardParams(Routes.Tasks, withSelectedProjectParams(urlParams), false);
    };

    const onUnassignedClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onAssignTasks();
    };

    // ---- render ----

    const hasGraph = location.taskGraph && location.taskGraph.length > 0;

    return (
        <div className={`location-summary-tile${isActive ? ' location-summary-tile--active' : ''}`} onClick={onClick}>

            {/* === Name column === */}
            <div className='lst-name-col'>
                <i className='fa-regular fa-thumbtack lst-pin-icon'></i>
                {/* Name and abbreviation run as one line of text, pipe-separated, and the
                    block — not the name alone — is what clamps to two lines, so the
                    abbreviation stays on the name's last line instead of under it. */}
                <div className='lst-name-text' title={location.name}>
                    <span className='lst-name'>{location.name}</span>
                    {location.abbreviation && (
                        <>
                            <span className='lst-name-sep' aria-hidden='true'>|</span>
                            <span className='lst-abbr'>{location.abbreviation}</span>
                        </>
                    )}
                </div>
            </div>

            {/* === 7 & 8. Health / Risk scores === */}
            <ScoreBlock
                label='Health'
                icon='fa-solid fa-heart-pulse'
                score={location.healthScore}
                unavailableReason={location.scoresUnavailableReason}
                higherIsBetter
            />
            {/* Risk is a magnitude, not a rating: 1 is low risk and 10 is high risk, so the
                bands run the opposite way from Health. */}
            <ScoreBlock
                label='Risk'
                icon='fa-regular fa-circle-exclamation'
                score={location.riskScore}
                unavailableReason={location.scoresUnavailableReason}
                higherIsBetter={false}
            />

            {/* === 9. Task Completion Graph === */}
            <div className='lst-graph-col'>
                <LoadingIndicator isLoading={!!isLoadingMetrics} hideWhileLoading>
                    {hasGraph && (
                        // stopPropagation belongs to the bar, not to the column: the column is
                        // the widest flexible region of the row, so swallowing clicks there
                        // would leave a ~250px dead strip on any location without a graph.
                        <div className='lst-graph' onClick={(e) => e.stopPropagation()}>
                            <NivoTaskCompletionBar
                                data={location.taskGraph ?? []}
                                onSegmentClick={onGraphBarClick}
                            />
                        </div>
                    )}
                </LoadingIndicator>
            </div>

            {/* Metrics — the wrapper exists so the flex chain from the tile down to
                .lst-metrics is unbroken. LoadingIndicator renders a plain <div> with no
                class, which would otherwise sit here as an unshrinkable flex item and let
                the stats overflow the tile. */}
            <div className='lst-metrics-wrap'>
                <LoadingIndicator isLoading={!!isLoadingMetrics}>
                    <div className='lst-metrics'>

                        {/* === Left metrics column === */}
                        <div className='lst-metrics-left'>

                            {/* 1. # Past Due Milestones */}
                            <div
                                className={`lst-datapoint lst-datapoint--clickable${location.pastDueMilestoneCount ? ' lst-datapoint--danger' : ''}`}
                                onClick={onPastDueMilestoneClick}
                                title='View past due milestones'
                            >
                                <i className='fa-regular fa-circle-exclamation lst-dp-icon' />
                                <span className='lst-dp-count'>{location.pastDueMilestoneCount ?? '—'}</span>
                                <span className='lst-dp-label'>Milestones Overdue</span>
                            </div>

                            {/* 2. # Past Due Tasks */}
                            <div
                                className={`lst-datapoint lst-datapoint--clickable${location.pastDueCount ? ' lst-datapoint--warning' : ''}`}
                                onClick={onPastDueClick}
                                title='View past due tasks'
                            >
                                <i className='fa-regular fa-triangle-exclamation lst-dp-icon' />
                                <span className='lst-dp-count'>{location.pastDueCount ?? '—'}</span>
                                <span className='lst-dp-label'>Tasks Overdue</span>
                            </div>

                            {/* 3. # Unassigned Tasks */}
                            <div
                                className='lst-datapoint lst-datapoint--unassigned lst-datapoint--clickable'
                                onClick={onUnassignedClick}
                                title='Assign tasks'
                            >
                                <i className='fa-regular fa-user lst-dp-icon' />
                                <span className='lst-dp-count'>{location.totalUnassignedCount ?? '—'}</span>
                                <span className='lst-dp-label'>Unassigned</span>
                            </div>

                        </div>

                        {/* === Right metrics column === */}
                        <div className='lst-metrics-right'>

                            {/* 4. # Milestones Due Soon */}
                            <div
                                className='lst-datapoint lst-datapoint--clickable'
                                onClick={onUpcomingMilestoneClick}
                                title='View upcoming milestones'
                            >
                                <i className='fa-regular fa-calendar lst-dp-icon' />
                                <span className='lst-dp-metric'>
                                    <span className='lst-dp-count'>{location.upcomingMilestoneCount ?? '—'}</span>
                                    <span className='lst-dp-label'>Milestones Due Soon</span>
                                </span>
                            </div>

                            {/* 5. % Overall Tasks completion */}
                            <div
                                className='lst-pct-row lst-pct-row--clickable'
                                onClick={onOverallCompleteClick}
                                title='View completed tasks'
                            >
                                <span className='lst-pct-row__label'>Project Overall:</span>
                                <span className='lst-pct-row__value'>
                                    {location.overallCompletionPct != null ? `${location.overallCompletionPct}%` : '—'}
                                </span>
                            </div>

                            {/* 6. % Overall Tasks completion To Date */}
                            <div
                                className='lst-pct-row lst-pct-row--clickable'
                                onClick={onCompleteToDateClick}
                                title='View tasks complete to date'
                            >
                                <span className='lst-pct-row__label'>Project to Date:</span>
                                <span className='lst-pct-row__value'>
                                    {location.completeToDatePct != null ? `${location.completeToDatePct}%` : '—'}
                                </span>
                            </div>

                        </div>

                    </div>
                </LoadingIndicator>
            </div>

            {/* Actions */}
            <div className='lst-actions-col'>
                <i className='fa-regular fa-ellipsis' onClick={(e) => e.stopPropagation()}></i>

                <button
                    type='button'
                    id={expandToggleId(location.id)}
                    className={`lst-expand-toggle${isExpanded ? ' lst-expand-toggle--open' : ''}`}
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Hide' : 'Show'} breakdown for ${location.name}`}
                    title={isExpanded ? 'Hide breakdown' : 'Show breakdown'}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpand?.();
                    }}>
                    <i className='fa-regular fa-chevron-down' />
                </button>
            </div>

        </div>
    );
};

export default LocationSummaryTile;
